// src/pages/auth/LockScreen.jsx
// ─────────────────────────────────────────────────────────────
// Shown instead of the app whenever the company's CM_Status is
// 2 (Demo Expired) or 4 (Activation Expired). Blocks every other
// screen — there is no way to reach the dashboard/sidebar from
// here until activation succeeds.
//
// Flow:
//   1. Check registration status via check_registration_status.php
//   2. Owner types the activation key received by email.
//   3. "Activate" → Register_Company_After_Key.php { CM_ID, CM_Key }
//      → on success, server sets CM_Status = 3 and extends expiry.
//   4. We refetch the company record (refreshCompany) so the lock
//      lifts immediately, with no full page reload.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { licenseAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import logo from "../../../assets/logo.png";

const STATUS_COPY = {
  2: {
    title: "Your Demo Has Expired",
    body: "The free trial period for TailorExpert has ended. Enter your activation key below to continue using the app.",
  },
  4: {
    title: "Your Activation Has Expired",
    body: "Your subscription has expired. Enter a new activation key below to keep using the app.",
  },
};

function formatRequestStatus(status, rejectReason) {
  const normalized = String(status ?? "").toLowerCase();
  if (normalized === "pending" || normalized === "pending approval") {
    return "Your registration is pending approval.";
  }
  if (normalized === "approved") {
    return "Your registration has been approved.";
  }
  if (normalized === "rejected") {
    return rejectReason
      ? `Your registration was rejected. Reason: ${rejectReason}`
      : "Your registration was rejected.";
  }
  return status ? `Registration status: ${status}` : "Registration status unavailable.";
}

export default function LockScreen() {
  const { company, refreshCompany, logout } = useAuth();

  const [checking,   setChecking]   = useState(false);
  const [key,        setKey]        = useState("");
  const [activating, setActivating] = useState(false);
  const [error,      setError]      = useState("");
  const [info,       setInfo]       = useState("");

  const copy = STATUS_COPY[Number(company?.CM_Status)] || {
    title: "Activation Required",
    body: "This app is locked. Enter your activation key below to continue.",
  };

  const cmId = company?.CM_Id ?? company?.CM_ID;

  // const handleCheckStatus = async () => {
  //   setChecking(true);
  //   setError("");
  //   setInfo("");
  //   try {
  //     const res = await licenseAPI.checkStatus();
  //     if (res?.status === "success" && res.data) {
  //       setInfo(formatRequestStatus(
  //         res.data.CM_Request_Status,
  //         res.data.CM_Reject_Reason,
  //       ));
  //     } else {
  //       // setError(res?.message || "Could not check registration status. Please try again.");
  //     }
  //   } catch (e) {
  //     console.error("[LockScreen] checkStatus:", e);
  //     // setError("Network error. Please check your connection and try again.");
  //   } finally {
  //     setChecking(false);
  //   }
  // };

  // useEffect(() => {
  //   handleCheckStatus();
  // }, []);

  const handleActivate = async () => {
    const trimmedKey = key.trim();
    if (!trimmedKey) {
      setError("Please enter your activation key.");
      return;
    }
    if (!cmId) {
      setError("Could not identify your company. Please contact support.");
      return;
    }

    setActivating(true);
    setError("");
    setInfo("");
    try {
      const res = await licenseAPI.activate({ cmId, key: trimmedKey });
      if (res?.status === "success") {
        const updated = await refreshCompany();
        if (!updated || Number(updated.CM_Status) !== 3) {
          setInfo(res.message || "App registered successfully. Refreshing…");
        }
      } else {
        setError(res?.message || "Incorrect activation key. Please try again.");
      }
    } catch (e) {
      console.error("[LockScreen] activate:", e);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setActivating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleActivate();
  };

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 440 }}>

        {/* Logo */}
        <div className="auth-logo">
          <img src={logo} alt="" className="auth-logo-icon" />
          <h1 className="auth-logo-title">TailorExpert</h1>
          <p className="auth-logo-sub">{copy.title}</p>
        </div>

        <p style={{
          fontSize: 13.5,
          color: "var(--text-muted)",
          textAlign: "center",
          marginBottom: 22,
          lineHeight: 1.5,
        }}>
          {copy.body}
        </p>

        {company?.CM_Name && (
          <div style={{
            background: "var(--surface2)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)", padding: "10px 14px", marginBottom: 18,
            fontSize: 13, color: "var(--text-muted)",
          }}>
            <div style={{ fontWeight: 700, color: "var(--text)", marginBottom: 2 }}>
              🏪 {company.CM_Name}
            </div>
          </div>
        )}

        {error && <div className="error-msg" style={{ marginBottom: 14 }}>{error}</div>}
        {!error && info && (
          <div style={{
            background: "var(--green-dim)", border: "1px solid rgba(76,175,122,0.3)",
            borderRadius: "var(--radius-sm)", padding: "9px 14px",
            color: "var(--green)", fontWeight: 600, fontSize: 12.5, marginBottom: 14,
          }}>
            ✅ {info}
          </div>
        )}

        <div className="auth-form">
          {/* <button
            className="btn btn-ghost btn-full"
            onClick={handleCheckStatus}
            disabled={checking}
            style={{ marginBottom: 16 }}
          >
            {checking
              ? <><span style={spinnerStyle} /> Checking Status…</>
              : "↻ Check Status"}
          </button> */}

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">
              Activation Key <span className="form-required">*</span>
            </label>
            <input
              className="form-input"
              placeholder="XXXX-XXXX-XXXX"
              value={key}
              onChange={(e) => { setKey(e.target.value); setError(""); }}
              onKeyDown={handleKeyDown}
              style={{ textAlign: "center", letterSpacing: 2, fontWeight: 600, textTransform: "uppercase" }}
              autoFocus
            />
          </div>

          <button
            className="btn btn-primary btn-lg btn-full"
            onClick={handleActivate}
            disabled={activating || !key.trim()}
          >
            {activating ? <><span style={spinnerStyle} /> Activating…</> : "🔓 Activate"}
          </button>

          <div style={{ textAlign: "center", marginTop: 18 }}>
            <span
              style={{ fontSize: 12.5, color: "var(--text-dim)", cursor: "pointer", textDecoration: "underline" }}
              onClick={logout}
            >
              Sign out
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const spinnerStyle = {
  display: "inline-block",
  width: 14, height: 14,
  border: "2px solid rgba(255,255,255,0.3)",
  borderTop: "2px solid #fff",
  borderRadius: "50%",
  animation: "spin 0.7s linear infinite",
  marginRight: 6,
};
